import { useCallback, useEffect, useRef, useState } from 'react';
import apiClient from '../../utils/apiClient';

const EMPTY = { tickets: [], total: 0, totalPages: 1, stats: null };

/**
 * One page of tickets from a support list endpoint, refetched whenever
 * `params` changes. A response that arrives after a newer request was made
 * is dropped, so typing in the search box can never leave an older, wider
 * result on screen.
 *
 * `params` is compared by value — pass a fresh object each render freely.
 */
export default function useTicketList(endpoint, params) {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const latest = useRef(0);
  const key = JSON.stringify(params);

  const load = useCallback(async () => {
    const request = latest.current + 1;
    latest.current = request;
    setLoading(true);
    try {
      const { data: res } = await apiClient.get(endpoint, { params: JSON.parse(key) });
      if (latest.current !== request) return;
      setData({
        tickets: res.tickets || [],
        total: res.total || 0,
        totalPages: res.totalPages || 1,
        stats: res.stats || null,
      });
      setError(null);
    } catch (err) {
      if (latest.current !== request) return;
      setError(err);
    } finally {
      if (latest.current === request) setLoading(false);
    }
  }, [endpoint, key]);

  useEffect(() => { load(); }, [load]);

  return { ...data, loading, error, reload: load };
}
